import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabaseApi, Project } from '../services/supabase';
import { 
  FolderOpen, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  Search
} from 'lucide-react';
import styled from 'styled-components';

const ProjectsContainer = styled.div`
  min-height: 100vh;
  background: #f8fafc;
`;

const Header = styled.header`
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 0 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #667eea;
`;

const MainContent = styled.div`
  display: flex;
  min-height: calc(100vh - 70px);
`;

const Sidebar = styled.aside`
  width: 250px;
  background: white;
  border-right: 1px solid #e2e8f0;
  padding: 24px 0;
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  color: #64748b;
  text-decoration: none;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8fafc;
    color: #667eea;
  }
  
  &.active {
    background: #f1f5f9;
    color: #667eea;
    border-right: 3px solid #667eea;
  }
`;

const Content = styled.main`
  flex: 1;
  padding: 24px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: #1e293b;
`;

const AddButton = styled(Link)`
  background: #667eea;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #5a67d8;
    transform: translateY(-1px);
  }
`;

const FiltersBar = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
`;

const FiltersContent = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  min-width: 300px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const ProjectsTable = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 100px;
  gap: 16px;
  padding: 16px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-weight: 600;
  color: #374151;
  font-size: 14px;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 100px;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  align-items: center;
  
  &:hover {
    background: #f8fafc;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const ProjectInfo = styled.div``;

const ProjectTitle = styled.h3`
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
`;

const ProjectDescription = styled.p`
  color: #64748b;
  font-size: 14px;
  line-height: 1.4;
`;



const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    switch (props.status) {
      case 'completed': return '#dcfce7';
      case 'in-progress': return '#fef3c7';
      case 'planning': return '#dbeafe';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'completed': return '#166534';
      case 'in-progress': return '#92400e';
      case 'planning': return '#1e40af';
      default: return '#374151';
    }
  }};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
  
  &.delete:hover {
    background: #fef2f2;
    color: #dc2626;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 8px;
  color: #374151;
`;

const EmptyDescription = styled.p`
  margin-bottom: 24px;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
`;

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    filterProjects();
  }, [projects, searchTerm, statusFilter]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const data = await supabaseApi.getProjects();
      setProjects(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterProjects = useCallback(() => {
    let filtered = projects;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(project =>
        project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.client?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(project => project.status === statusFilter);
    }

    setFilteredProjects(filtered);
  }, [projects, searchTerm, statusFilter]);

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        const success = await supabaseApi.deleteProject(id);
        if (success) {
          setProjects(projects.filter(p => p.id !== id));
        }
      } catch (error) {
        console.error('Error deleting project:', error);
      }
    }
  };

  if (loading) {
    return (
      <ProjectsContainer>
        <Header>
          <HeaderContent>
            <Logo>🚀 Portfolio Admin</Logo>
          </HeaderContent>
        </Header>
        <MainContent>
          <Sidebar>
            <NavItem to="/">
              <FolderOpen size={20} />
              Dashboard
            </NavItem>
            <NavItem to="/projects" className="active">
              <FolderOpen size={20} />
              Projects
            </NavItem>
            <NavItem to="/categories">
              <FolderOpen size={20} />
              Categories
            </NavItem>
          </Sidebar>
          <Content>
            <LoadingState>Loading projects...</LoadingState>
          </Content>
        </MainContent>
      </ProjectsContainer>
    );
  }

  return (
    <ProjectsContainer>
      <Header>
        <HeaderContent>
          <Logo>🚀 Portfolio Admin</Logo>
        </HeaderContent>
      </Header>

      <MainContent>
        <Sidebar>
          <NavItem to="/">
            <FolderOpen size={20} />
            Dashboard
          </NavItem>
          <NavItem to="/projects" className="active">
            <FolderOpen size={20} />
            Projects
          </NavItem>
          <NavItem to="/categories">
            <FolderOpen size={20} />
            Categories
          </NavItem>
        </Sidebar>

        <Content>
          <PageHeader>
            <PageTitle>Projects</PageTitle>
            <AddButton to="/add-project">
              <Plus size={20} />
              Add Project
            </AddButton>
          </PageHeader>

          <FiltersBar>
            <FiltersContent>
              <SearchInput>
                <SearchIcon>
                  <Search size={16} />
                </SearchIcon>
                <Input
                  type="text"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </SearchInput>
              
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="in-progress">In Progress</option>
                <option value="planning">Planning</option>
              </Select>
            </FiltersContent>
          </FiltersBar>

          <ProjectsTable>
            <TableHeader>
              <div>Project</div>
              <div>Client</div>
              <div>Duration</div>
              <div>Budget</div>
              <div>Status</div>
              <div>Actions</div>
            </TableHeader>

            {filteredProjects.length === 0 ? (
              <EmptyState>
                <EmptyIcon>📁</EmptyIcon>
                <EmptyTitle>No projects found</EmptyTitle>
                <EmptyDescription>
                  {searchTerm || statusFilter !== 'all' 
                    ? 'Try adjusting your filters' 
                    : 'Get started by adding your first project'
                  }
                </EmptyDescription>
                {!searchTerm && statusFilter === 'all' && (
                  <AddButton to="/add-project">
                    <Plus size={20} />
                    Add Project
                  </AddButton>
                )}
              </EmptyState>
            ) : (
              filteredProjects.map((project) => (
                <TableRow key={project.id}>
                  <ProjectInfo>
                    <ProjectTitle>{project.title}</ProjectTitle>
                    <ProjectDescription>
                      {project.short_description || project.description.substring(0, 100)}...
                    </ProjectDescription>
                  </ProjectInfo>
                  
                  <div>{project.client || 'N/A'}</div>
                  <div>{project.duration || 'N/A'}</div>
                  <div>{project.budget || 'N/A'}</div>
                  
                  <StatusBadge status={project.status}>
                    {project.status}
                  </StatusBadge>
                  
                  <Actions>
                    <ActionButton 
                      title="View"
                      onClick={() => navigate(`/project/${project.id}`)}
                    >
                      <Eye size={16} />
                    </ActionButton>
                    <ActionButton 
                      title="Edit"
                      onClick={() => navigate(`/edit-project/${project.id}`)}
                    >
                      <Edit size={16} />
                    </ActionButton>
                    <ActionButton 
                      title="Delete" 
                      className="delete"
                      onClick={() => handleDelete(project.id)}
                    >
                      <Trash2 size={16} />
                    </ActionButton>
                  </Actions>
                </TableRow>
              ))
            )}
          </ProjectsTable>
        </Content>
      </MainContent>
    </ProjectsContainer>
  );
};

export default Projects;
